import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Sparkles,
  FolderKanban,
  ClipboardList,
  StickyNote,
  CalendarClock,
  Users,
  Rocket,
  ArrowRight,
  ArrowLeft,
  X,
} from "lucide-react";

export const DEMO_TOUR_FLAG = "serpix_demo_tour";

const STEPS = [
  {
    icon: Sparkles,
    title: "Serpix demo turuna hoş geldin! 👋",
    desc: "Bu hesap gerçekçi örnek verilerle dolu: projeler, görevler, notlar ve aktiviteler seni bekliyor. 1 dakikada öne çıkan özellikleri gezdirelim.",
  },
  {
    selector: '[data-testid="sidebar-projects-link"]',
    icon: FolderKanban,
    title: "Projeler",
    desc: "\u201CWeb Sitesi Tasarım\u201D ve \u201COfis Tadilat\u201D örnek projelerini incele — gruplar, gecikmiş görev rozetleri ve Kanban panosu burada.",
  },
  {
    selector: '[data-testid="sidebar-tasks-hub"]',
    icon: ClipboardList,
    title: "Görevlerim",
    desc: "Sana atanan tüm görevler tarihe göre tek listede. Filtre çipleriyle gecikmişleri ve bu hafta bitenleri anında gör.",
  },
  {
    selector: '[data-testid="sidebar-quick-notes"]',
    icon: StickyNote,
    title: "Hızlı Not",
    desc: "Aklına geleni saniyeler içinde not al, yapılacaklara dönüştür. Örnek notlarını ekledik bile.",
  },
  {
    selector: '[data-testid="sidebar-my-activities"]',
    icon: CalendarClock,
    title: "Aktivitelerim",
    desc: "Müşteri görüşmeleri ve planlı aktiviteler (arama kayıtları, hatırlatmalar) CRM tarzında burada takip edilir.",
  },
  {
    selector: '[data-testid="scope-toggle"]',
    icon: Users,
    title: "Ben / Herkes",
    desc: "Tek tıkla yalnızca kendi işlerine odaklan ya da tüm şirketin görünümüne geç. Tüm sayfalar bu seçime uyar.",
  },
  {
    icon: Rocket,
    title: "Keşfetmeye hazırsın! 🚀",
    desc: "İstediğin gibi kurcala — bu demo her gece tertemiz haline döner. Beğendiysen sağ üstteki \u201CYükselt\u201D ile kendi hesabını başlatabilirsin.",
  },
];

export default function DemoTour({ onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const current = STEPS[step];

  const measure = useCallback(() => {
    if (!current.selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(current.selector);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "nearest" });
    const r = el.getBoundingClientRect();
    // Element hidden (mobile drawer closed etc.) → fall back to centered card.
    if (!r.width || !r.height || r.right < 0 || r.left > window.innerWidth) {
      setRect(null);
      return;
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [current.selector]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const cardStyle = useMemo(() => {
    if (!rect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const CARD_W = 340;
    const pad = 14;
    // Prefer right of the target (sidebar items); flip below for topbar.
    let left = rect.left + rect.width + pad;
    let top = rect.top;
    if (left + CARD_W > window.innerWidth - 8) {
      left = Math.max(8, Math.min(rect.left, window.innerWidth - CARD_W - 8));
      top = rect.top + rect.height + pad;
    }
    if (top + 220 > window.innerHeight) {
      top = Math.max(8, window.innerHeight - 240);
    }
    return { top, left };
  }, [rect]);

  const finish = () => onClose();
  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : finish());
  const back = () => step > 0 && setStep(step - 1);
  const Icon = current.icon;

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="demo-tour">
      {/* Backdrop / spotlight hole */}
      {rect ? (
        <div
          className="pointer-events-none fixed rounded-xl ring-2 ring-violet-400 transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(24,24,27,0.62)",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-zinc-900/60" />
      )}

      {/* Card */}
      <div
        className="fixed w-[340px] max-w-[calc(100vw-16px)] rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl transition-all duration-300"
        style={cardStyle}
        data-testid="demo-tour-card"
      >
        <button
          type="button"
          onClick={finish}
          data-testid="demo-tour-close"
          aria-label="Turu kapat"
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-100">
          <Icon className="h-5 w-5 text-violet-700" />
        </div>
        <h3 className="mt-3 font-display text-lg font-semibold text-zinc-950">
          {current.title}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-zinc-600">{current.desc}</p>

        <div className="mt-4 flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={[
                "h-1.5 flex-1 rounded-full transition-colors",
                i <= step ? "bg-violet-600" : "bg-zinc-200",
              ].join(" ")}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={finish}
            data-testid="demo-tour-skip"
            className="text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-700"
          >
            Turu atla
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={back}
                data-testid="demo-tour-back"
                className="inline-flex h-9 items-center gap-1 rounded-lg border border-zinc-200 px-3 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Geri
              </button>
            )}
            <button
              type="button"
              onClick={next}
              data-testid="demo-tour-next"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-violet-700"
            >
              {step === STEPS.length - 1 ? "Keşfetmeye başla" : "İleri"}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
